/* Native touch scrolling; pointer dragging is only used for a mouse. */
(() => {
  if (window.zooFeaturesInitialized) return;
  window.zooFeaturesInitialized = true;
  const instances = new WeakMap();
  function initialize(list) {
    if (instances.has(list)) return;
    const mobile = window.matchMedia('(max-width: 767px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const cards = Array.from(list.children);
    let selected = Math.min(1, cards.length - 1);
    let frame = 0;
    let gesture = null;
    let lastWidth = -1;
    let lastMobile = null;
    function nearest() {
      const center = list.getBoundingClientRect().left + list.clientWidth / 2;
      return cards.reduce((best, card, index) => {
        const distance = Math.abs(card.getBoundingClientRect().left + card.offsetWidth / 2 - center);
        return distance < best.distance ? { index, distance } : best;
      }, { index: 0, distance: Infinity }).index;
    }
    function release() {
      const id = gesture?.id;
      gesture = null;
      if (id !== undefined && list.hasPointerCapture(id)) list.releasePointerCapture(id);
      list.classList.remove('is-dragging');
    }
    function settle(index) {
      selected = Math.max(0, Math.min(cards.length - 1, index));
      list.scrollTo({ left: target(selected), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    }
    function target(index) {
      return cards[index].getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft - (list.clientWidth - cards[index].offsetWidth) / 2;
    }
    function align() {
      // Lazy images and font loading can change height during a swipe.
      // Recenter only when the horizontal layout actually changes.
      if (list.clientWidth === lastWidth && mobile.matches === lastMobile) return;
      lastWidth = list.clientWidth;
      lastMobile = mobile.matches;
      release();
      list.tabIndex = mobile.matches ? 0 : -1;
      list.scrollTo({ left: mobile.matches ? target(selected) : 0, behavior: 'instant' });
    }
    const observer = new ResizeObserver(align);
    observer.observe(list);
    list.addEventListener('keydown', event => {
      if (!mobile.matches || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') selected = 0;
      else if (event.key === 'End') selected = cards.length - 1;
      else selected = Math.max(0, Math.min(cards.length - 1, selected + (event.key === 'ArrowRight' ? 1 : -1)));
      list.scrollTo({ left: target(selected), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    });
    list.addEventListener('pointerdown', event => {
      // Touch and pen scrolling belong to the browser: never capture or reset them.
      if (event.pointerType !== 'mouse' || !mobile.matches || !event.isPrimary || event.button !== 0) return;
      // Stop a previous smooth scroll at the point where the user takes over.
      list.scrollTo({ left: list.scrollLeft, behavior: 'instant' });
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, start: list.scrollLeft, index: nearest(), dx: 0, dragging: false };
    });
    list.addEventListener('pointermove', event => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      if (!gesture.dragging) {
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { release(); return; }
        if (Math.abs(dx) < 8) return;
        gesture.dragging = true;
        list.setPointerCapture(event.pointerId);
        list.classList.add('is-dragging');
      }
      gesture.dx = dx;
      list.scrollLeft = gesture.start - dx;
    });
    function finish(event) {
      if (!gesture || event.pointerId !== gesture.id) return;
      const { dragging, dx, index } = gesture;
      let destination = nearest();
      if (dragging && event.type !== 'pointercancel' && Math.abs(dx) > 35 && destination === index) destination = index + (dx < 0 ? 1 : -1);
      release();
      if (dragging) settle(destination);
    }
    list.addEventListener('pointerup', finish);
    list.addEventListener('pointercancel', finish);
    list.addEventListener('lostpointercapture', finish);
    list.addEventListener('pointerleave', () => { if (gesture && !gesture.dragging) release(); });
    list.addEventListener('dragstart', event => { if (mobile.matches) event.preventDefault(); });
    list.addEventListener('scroll', () => {
      if (!mobile.matches || gesture) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { selected = nearest(); });
    }, { passive: true });
    instances.set(list, () => { observer.disconnect(); cancelAnimationFrame(frame); release(); instances.delete(list); });
    align();
  }
  function mount(container) { container.querySelectorAll('.feature-grid, .difference-grid').forEach(initialize); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.feature-grid, .difference-grid').forEach(list => instances.get(list)?.());
  });
})();
